import { useQuery } from "@tanstack/react-query";
import { api, FESTIVAL_META, CATEGORY_LABELS } from "@/lib/api";
import { useState, useMemo } from "react";
import { Lock, Flame, Sparkles, Gem, Zap, TrendingUp, Music, Play } from "lucide-react";
import { Link } from "react-router-dom";

const CATEGORY_TABS = [
  { id: "all", label: "Alle" },
  { id: "headliner", label: "Headliner" },
  { id: "international", label: "International" },
  { id: "danish", label: "Danske" },
];

const FEST_TABS = [
  { id: "all", label: "Alle festivaler" },
  { id: "roskilde", label: "Roskilde" },
  { id: "northside", label: "NorthSide" },
  { id: "syd_for_solen", label: "Syd for Solen" },
  { id: "tinderbox", label: "Tinderbox" },
  { id: "smukfest", label: "Smukfest" },
];

const CAT_ACCENT = {
  headliner: "#F59E0B",
  international: "#60A5FA",
  danish: "#F87171",
  orange_opener: "#FF4500",
};

function PctBar({ pct, color = "#FFFFFF" }) {
  return (
    <div className="h-1.5 bg-white/5 rounded-full overflow-hidden mt-1">
      <div className="h-full rounded-full" style={{ width: `${Math.min(pct, 100)}%`, background: color }} />
    </div>
  );
}

function ArtistThumb({ artist, className = "w-12 h-12" }) {
  if (artist?.image_url) return <img src={artist.image_url} alt="" className={`${className} rounded-md object-cover flex-shrink-0`} />;
  return <div className={`${className} rounded-md bg-zinc-800 flex items-center justify-center flex-shrink-0`}><Music size={14} className="text-zinc-500" /></div>;
}

function ArtistRow({ rank, artist, pct, count, right, badge, badgeColor }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <div className="flex items-center gap-4 px-4 py-3">
        <div className="font-score text-2xl w-8 text-zinc-500">{rank}</div>
        <ArtistThumb artist={artist} className="w-12 h-12" />
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-3">
            <div className="font-medium truncate">{artist?.name}</div>
            <div className="font-score text-xl flex-shrink-0">{pct}%</div>
          </div>
          <div className="mt-1 flex items-center gap-3">
            <span className="text-xs text-zinc-500">{count} spiller{count === 1 ? "" : "e"}</span>
            {badge && <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: `${badgeColor}20`, color: badgeColor }}>{badge}</span>}
          </div>
          <PctBar pct={pct} color={badgeColor || "#FFFFFF"} />
        </div>
        {artist?.spotify_id && (
          <button onClick={() => setOpen(v => !v)} className={`p-1.5 rounded ${open ? "text-emerald-400" : "text-zinc-500"} hover:bg-white/5`}>
            <Play size={14} />
          </button>
        )}
        {right}
      </div>
      {open && artist?.spotify_id && (
        <iframe src={`https://open.spotify.com/embed/artist/${artist.spotify_id}?theme=0`} width="100%" height="152" frameBorder="0"
          allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture" loading="lazy" title={artist.name} className="border-b border-white/5" />
      )}
    </>
  );
}

function Section({ title, icon, subtitle, testId, children }) {
  return (
    <section className="mt-14" data-testid={testId}>
      <div className="flex items-center gap-3 mb-2">
        {icon}
        <h2 className="font-display font-bold text-2xl sm:text-3xl">{title}</h2>
      </div>
      {subtitle && <p className="text-sm text-zinc-400 mb-5 max-w-2xl">{subtitle}</p>}
      {!subtitle && <div className="mb-5" />}
      {children}
    </section>
  );
}

export default function Statistics() {
  const { data, isLoading } = useQuery({ queryKey: ["stats"], queryFn: () => api.get("/statistics").then(r => r.data), retry: 0 });
  const [festival, setFestival] = useState("all");
  const [category, setCategory] = useState("all");

  const filteredOverall = useMemo(() => {
    if (!data?.overall_top_artists) return [];
    let list = data.overall_top_artists;
    if (festival !== "all") list = list.filter(x => x.festival_ids.includes(festival));
    if (category !== "all") list = list.filter(x => x.top_category === category);
    return list;
  }, [data, festival, category]);

  if (isLoading) return <div className="max-w-4xl mx-auto p-8 text-zinc-500">Indlæser statistik…</div>;

  // LOCKED SCREEN
  if (data?.locked) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="label-eyebrow">Community Picks</div>
        <h1 className="hero-title text-4xl sm:text-5xl mt-2">Statistik</h1>
        <div className="mt-10 card-flat p-10 sm:p-16 text-center" data-testid="stats-locked">
          <div className="w-16 h-16 rounded-full bg-white/5 mx-auto flex items-center justify-center mb-6">
            <Lock size={24} className="text-zinc-400" />
          </div>
          <h2 className="font-display font-bold text-3xl mb-3">🔒 Låst indtil deadline</h2>
          <p className="text-zinc-400 max-w-md mx-auto text-lg">{data.message}</p>
          <p className="text-zinc-500 max-w-md mx-auto mt-4">Indtil da kan du ikke se, hvad de andre spillere har gættet.</p>
          <div className="mt-8 text-sm text-zinc-500">
            Åbner: <span className="text-white font-medium">{new Date(data.deadline).toLocaleString("da-DK")}</span>
          </div>
        </div>
      </div>
    );
  }

  // Too few players
  if (data && !data.min_players_reached) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="label-eyebrow">Community Picks</div>
        <h1 className="hero-title text-4xl sm:text-5xl mt-2">Statistik</h1>
        <div className="mt-10 card-flat p-10 text-center" data-testid="stats-not-enough">
          <div className="font-display font-bold text-2xl mb-2">Endnu ikke nok data</div>
          <p className="text-zinc-400">Community statistics vises når der er indsendt mindst {data.min_players_threshold} spilleres forudsigelser.</p>
          <div className="mt-4 text-sm text-zinc-500">Aktuelt: {data.total_submitted_players} spiller{data.total_submitted_players === 1 ? "" : "e"}</div>
        </div>
      </div>
    );
  }

  const activeFestivals = festival === "all" ? Object.keys(data.by_festival) : [festival];

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="stats-page">
      {/* Hero */}
      <div className="label-eyebrow">Community Picks</div>
      <h1 className="hero-title text-4xl sm:text-5xl lg:text-6xl mt-2">Hvad gættede alle på?</h1>
      {data.admin_preview && (
        <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-orange-500/30 bg-orange-500/10 text-orange-300 text-xs font-semibold" data-testid="stats-admin-preview-badge">
          <span className="h-1.5 w-1.5 rounded-full bg-orange-400" /> ADMIN PREVIEW · Før deadline
        </div>
      )}
      <p className="mt-4 text-zinc-400 max-w-2xl">Aggregeret oversigt over hvad {data.total_submitted_players} spillere har gættet på. Ingen individuelle valg afsløres.</p>

      {/* Hero stats */}
      {data.consensus && (
        <div className="mt-8 card-flat p-6 sm:p-8" data-testid="stats-consensus">
          <div className="flex items-center gap-2 text-orange-400">
            <Flame size={16} />
            <span className="label-eyebrow" style={{ color: "#FB923C" }}>Community consensus</span>
          </div>
          <div className="mt-4 flex flex-col sm:flex-row items-start sm:items-center gap-6">
            <div className="flex items-center gap-4 flex-1 min-w-0">
              <ArtistThumb artist={data.consensus.artist} className="w-16 h-16 sm:w-20 sm:h-20" />
              <div className="min-w-0">
                <div className="font-display font-bold text-2xl sm:text-3xl truncate">{data.consensus.artist.name}</div>
                <div className="text-sm text-zinc-400 mt-1">Gættet af <span className="text-white font-semibold">{data.consensus.player_pct}%</span> af spillerne på {data.consensus.festival_ids.length} festival{data.consensus.festival_ids.length === 1 ? "" : "er"}</div>
              </div>
            </div>
            <div className="font-score text-5xl sm:text-7xl text-orange-400 leading-none">{data.consensus.player_pct}%</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="mt-10 flex flex-col gap-3">
        <div className="flex gap-2 overflow-x-auto pb-1">
          {FEST_TABS.map(t => (
            <button key={t.id} onClick={() => setFestival(t.id)}
              className={`px-4 py-2 rounded-full text-sm whitespace-nowrap border ${festival === t.id ? "bg-white text-black border-white" : "text-zinc-400 border-white/10 hover:text-white"}`}
              data-testid={`stats-fest-${t.id}`}>{t.label}</button>
          ))}
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {CATEGORY_TABS.map(t => (
            <button key={t.id} onClick={() => setCategory(t.id)}
              className={`px-4 py-2 rounded-full text-sm whitespace-nowrap border ${category === t.id ? "bg-white text-black border-white" : "text-zinc-400 border-white/10 hover:text-white"}`}
              data-testid={`stats-cat-${t.id}`}>{t.label}</button>
          ))}
        </div>
      </div>

      {/* WILDEST PLAYERS - main feature */}
      {data.wildest_players?.length > 0 && (
        <Section
          title="De vildeste gæt"
          icon={<Zap size={24} className="text-orange-400" />}
          subtitle="Spillere der har turdet gå helt amok. Deres mest unikke valg – som ingen andre gættede."
          testId="stats-wildest"
        >
          <div className="grid gap-4 md:grid-cols-2">
            {data.wildest_players.map((w, idx) => (
              <Link to={`/u/${w.user_id}`} key={w.user_id} className="card-flat p-5 interactive hover:-translate-y-0.5" data-testid={`stats-wildest-${w.user_id}`}>
                <div className="flex items-center gap-3 mb-4">
                  {w.effective_picture ? (
                    <img src={w.effective_picture} alt="" className="w-10 h-10 rounded-full object-cover" onError={e => e.currentTarget.style.display = "none"} />
                  ) : <div className="w-10 h-10 rounded-full bg-zinc-800" />}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{w.display_name}</div>
                    <div className="text-xs text-orange-400 uppercase tracking-widest">🧨 gik helt amok</div>
                  </div>
                  <div className="font-score text-3xl text-orange-400">#{idx + 1}</div>
                </div>
                <div className="flex items-center gap-3 rounded-lg bg-white/5 p-3">
                  <ArtistThumb artist={w.wildest_pick.artist} className="w-14 h-14" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{w.wildest_pick.artist.name}</div>
                    <div className="text-xs text-zinc-400 mt-0.5">
                      → {FESTIVAL_META[w.wildest_pick.festival_id]?.name} · <span className="capitalize">{w.wildest_pick.category === "orange_opener" ? "Orange åbner" : w.wildest_pick.category}</span>
                    </div>
                  </div>
                </div>
                <div className="mt-3 text-sm text-zinc-400">
                  Kun <span className="text-white font-semibold">{w.wildest_pick.count} ud af {w.wildest_pick.festival_players}</span> spillere havde samme idé ({w.wildest_pick.pct}%)
                </div>
              </Link>
            ))}
          </div>
        </Section>
      )}

      {/* You vs community */}
      {data.you_vs_community && (data.you_vs_community.most_agreed.length > 0 || data.you_vs_community.most_unique.length > 0) && (
        <Section title="Dig vs. fællesskabet" icon={<TrendingUp size={24} className="text-emerald-400" />} testId="stats-you-vs">
          <div className="grid gap-6 md:grid-cols-2">
            <div className="card-flat overflow-hidden" data-testid="stats-you-agreed">
              <div className="px-4 py-3 border-b border-white/5 flex items-center gap-2 text-emerald-400">
                <Flame size={16} /> <span className="label-eyebrow" style={{ color: "#34D399" }}>Det har alle gættet</span>
              </div>
              <div className="divide-y divide-white/5">
                {data.you_vs_community.most_agreed.map((p, i) => (
                  <ArtistRow key={`${p.artist.artist_id}-${i}`} rank={i + 1} artist={p.artist} pct={p.pct} count={p.count} badge="👀 POPULAR" badgeColor="#34D399" />
                ))}
                {data.you_vs_community.most_agreed.length === 0 && <div className="p-4 text-zinc-500 text-sm">Ingen valg endnu.</div>}
              </div>
            </div>
            <div className="card-flat overflow-hidden" data-testid="stats-you-unique">
              <div className="px-4 py-3 border-b border-white/5 flex items-center gap-2 text-orange-400">
                <Zap size={16} /> <span className="label-eyebrow" style={{ color: "#FB923C" }}>Dine mest unikke gæt</span>
              </div>
              <div className="divide-y divide-white/5">
                {data.you_vs_community.most_unique.map((p, i) => (
                  <ArtistRow key={`${p.artist.artist_id}-${i}`} rank={i + 1} artist={p.artist} pct={p.pct} count={p.count} badge="🧨 BOLD" badgeColor="#FB923C" />
                ))}
                {data.you_vs_community.most_unique.length === 0 && <div className="p-4 text-zinc-500 text-sm">Ingen valg endnu.</div>}
              </div>
            </div>
          </div>
        </Section>
      )}

      {/* Overall top picks */}
      <Section
        title="De mest populære gæt"
        icon={<Flame size={24} className="text-orange-400" />}
        subtitle="Hvem har flest spillere gættet på – på tværs af festivaler og kategorier."
        testId="stats-overall-top"
      >
        <div className="card-flat overflow-hidden divide-y divide-white/5">
          {filteredOverall.map((x, i) => (
            <ArtistRow
              key={x.artist.artist_id}
              rank={i + 1}
              artist={x.artist}
              pct={x.player_pct}
              count={x.player_count}
              badge={i < 3 ? "🔥 HOT PICK" : null}
              badgeColor="#FB923C"
              right={
                <div className="hidden sm:flex flex-col items-end text-right ml-2">
                  <div className="text-xs text-zinc-500">
                    {x.festival_ids.slice(0, 3).map(fid => (
                      <span key={fid} className="inline-block ml-1 w-2 h-2 rounded-full" style={{ background: FESTIVAL_META[fid]?.color }} />
                    ))}
                  </div>
                  <div className="text-xs text-zinc-500 capitalize mt-1">{x.top_category}</div>
                </div>
              }
            />
          ))}
          {filteredOverall.length === 0 && <div className="p-6 text-zinc-500 text-sm">Ingen resultater for dette filter.</div>}
        </div>
      </Section>

      {/* Community favorites across festivals */}
      {data.community_favorites?.length > 0 && (
        <Section
          title="Community's favorites"
          icon={<Sparkles size={24} className="text-purple-400" />}
          subtitle="Artister som mange har valgt på flere forskellige festivaler."
          testId="stats-favorites"
        >
          <div className="grid gap-3 sm:grid-cols-2">
            {data.community_favorites.map(x => (
              <div key={x.artist.artist_id} className="card-flat p-4 flex items-center gap-4" data-testid={`stats-fav-${x.artist.artist_id}`}>
                <ArtistThumb artist={x.artist} className="w-14 h-14" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{x.artist.name}</div>
                  <div className="text-xs text-zinc-500 mt-0.5">{x.total_picks} valg på {x.festival_ids.length} festivaler</div>
                  <div className="mt-2 flex gap-1">
                    {x.festival_ids.map(fid => (
                      <span key={fid} className="text-xs px-2 py-0.5 rounded" style={{ background: `${FESTIVAL_META[fid]?.color}20`, color: FESTIVAL_META[fid]?.color }}>
                        {FESTIVAL_META[fid]?.short}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="font-score text-3xl">{x.festival_ids.length}×</div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Bold picks / under the radar */}
      {data.bold_picks?.length > 0 && (
        <Section
          title="Under the radar"
          icon={<Gem size={24} className="text-blue-400" />}
          subtitle="Artister som kun få har turdet gætte på. Måske et gennembrud – måske ren fantasi."
          testId="stats-bold"
        >
          <div className="card-flat overflow-hidden divide-y divide-white/5">
            {data.bold_picks.map((x, i) => (
              <ArtistRow
                key={x.artist.artist_id}
                rank={i + 1}
                artist={x.artist}
                pct={x.player_pct}
                count={x.player_count}
                badge="💎 UNDER THE RADAR"
                badgeColor="#60A5FA"
              />
            ))}
          </div>
        </Section>
      )}

      {/* Per festival */}
      <Section title="Fordeling pr. festival" icon={<TrendingUp size={24} className="text-white" />} testId="stats-per-festival">
        <div className="space-y-6">
          {activeFestivals.map(fid => {
            const f = data.by_festival[fid];
            const meta = FESTIVAL_META[fid];
            if (!f) return null;
            const columns = [
              category === "all" || category === "headliner" ? { key: "top_headliners", label: "Headliners", color: CAT_ACCENT.headliner } : null,
              category === "all" || category === "international" ? { key: "top_international", label: "International", color: CAT_ACCENT.international } : null,
              category === "all" || category === "danish" ? { key: "top_danish", label: "Danske", color: CAT_ACCENT.danish } : null,
            ].filter(Boolean);
            return (
              <div key={fid} className="card-flat overflow-hidden" data-testid={`stats-fest-block-${fid}`}>
                <div className="px-5 py-4 flex items-center justify-between" style={{ borderLeft: `3px solid ${meta.color}` }}>
                  <div className="flex items-center gap-3">
                    <div className="h-1 w-8" style={{ background: meta.color }} />
                    <div>
                      <div className="font-display font-bold text-2xl">{meta.name}</div>
                      <div className="text-xs text-zinc-500">{f.player_count} spiller{f.player_count === 1 ? "" : "e"} med valg her</div>
                    </div>
                  </div>
                </div>
                <div className="grid md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-white/5">
                  {columns.map(col => (
                    <div key={col.key}>
                      <div className="px-4 py-3 border-b border-white/5">
                        <div className="label-eyebrow" style={{ color: col.color }}>{col.label}</div>
                      </div>
                      <div className="divide-y divide-white/5">
                        {f[col.key].slice(0, 5).map((x, i) => (
                          <ArtistRow key={`${col.key}-${x.artist.artist_id}`} rank={i + 1} artist={x.artist} pct={x.pct} count={x.count} badgeColor={col.color} />
                        ))}
                        {f[col.key].length === 0 && <div className="p-4 text-zinc-500 text-sm">Ingen data.</div>}
                      </div>
                    </div>
                  ))}
                </div>
                {fid === "roskilde" && f.top_orange_openers?.length > 0 && (category === "all") && (
                  <div className="border-t border-white/5">
                    <div className="px-4 py-3 border-b border-white/5">
                      <div className="label-eyebrow" style={{ color: CAT_ACCENT.orange_opener }}>🟠 Orange Scene åbner</div>
                    </div>
                    <div className="divide-y divide-white/5">
                      {f.top_orange_openers.slice(0, 5).map((x, i) => (
                        <ArtistRow key={x.artist.artist_id} rank={i + 1} artist={x.artist} pct={x.pct} count={x.count} badgeColor={CAT_ACCENT.orange_opener} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Section>
    </div>
  );
}
